import { createFileRoute, Link, useNavigate, useRouter } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { notifyAdminRegistered } from "@/lib/admin.functions";

export const Route = createFileRoute("/auth/set-password")({
  ssr: false,
  head: () => ({ meta: [{ title: "Set password, Casa Mi Pajobe" }, { name: "robots", content: "noindex" }] }),
  component: SetPasswordPage,
  errorComponent: ({ error, reset }) => {
    const router = useRouter();
    return (
      <div className="mx-auto max-w-md p-12 text-center">
        <p>{error.message}</p>
        <button onClick={() => { router.invalidate(); reset(); }} className="mt-4 underline">Retry</button>
      </div>
    );
  },
  notFoundComponent: () => <div className="p-12 text-center text-muted-foreground">Not found.</div>,
});

function SetPasswordPage() {
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);
  const [email, setEmail] = useState<string | null>(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    // Supabase parses the invite/recovery token from the URL hash on load
    // and signs the user in. Wait for that, then show the form.
    const t = setTimeout(async () => {
      const { data } = await supabase.auth.getUser();
      if (!data.user) {
        setErr("Deze uitnodigingslink is ongeldig of verlopen. Vraag een nieuwe aan.");
      } else {
        setEmail(data.user.email ?? null);
      }
      setReady(true);
    }, 400);
    return () => clearTimeout(t);
  }, []);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    if (password.length < 8) return setErr("Minimaal 8 tekens.");
    if (password !== confirm) return setErr("Wachtwoorden komen niet overeen.");
    setBusy(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      try {
        await notifyAdminRegistered();
      } catch (e) {
        console.error("[set-password] notifyAdminRegistered failed", e);
      }
      navigate({ to: "/admin" });
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Mislukt.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border/70 bg-background/80 backdrop-blur">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-5">
          <Link to="/" className="font-display text-xl font-semibold">Casa Mi Pajobe</Link>
        </div>
      </header>
      <main className="mx-auto max-w-md px-6 py-20">
        <h1 className="text-3xl font-semibold">Kies je wachtwoord</h1>
        {email && <p className="mt-2 text-sm text-muted-foreground">Account: {email}</p>}

        {!ready ? (
          <p className="mt-6 text-sm text-muted-foreground">Even geduld…</p>
        ) : err && !email ? (
          <div className="mt-6 rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">{err}</div>
        ) : (
          <form onSubmit={onSubmit} className="mt-8 space-y-4 rounded-2xl border border-border/70 bg-card p-6 shadow-sm">
            {err && <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">{err}</div>}
            <label className="block">
              <span className="text-sm font-medium">Nieuw wachtwoord</span>
              <input type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)}
                className="mt-1 w-full rounded-lg border border-input bg-background px-3 py-2.5 outline-none focus:border-primary focus:ring-2 focus:ring-primary/30" />
            </label>
            <label className="block">
              <span className="text-sm font-medium">Bevestig wachtwoord</span>
              <input type="password" required minLength={8} value={confirm} onChange={(e) => setConfirm(e.target.value)}
                className="mt-1 w-full rounded-lg border border-input bg-background px-3 py-2.5 outline-none focus:border-primary focus:ring-2 focus:ring-primary/30" />
            </label>
            <button type="submit" disabled={busy}
              className="w-full rounded-full bg-primary px-6 py-2.5 text-sm font-medium text-primary-foreground disabled:opacity-60">
              {busy ? "…" : "Wachtwoord instellen"}
            </button>
          </form>
        )}
      </main>
    </div>
  );
}